import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { ChevronDown, ChevronRight, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Collapsible "Filing readiness" drawer that sits under the Daily
 * Tracking hero ribbon. Renders a compact one-line summary when
 * collapsed ("2 of 3 facilities complete for June 2026") and lazy-loads
 * a per-facility progress list when the user expands it — so we don't
 * make the endpoint call on every page load.
 *
 * Purpose: give owners visibility into WHICH facility × WHICH days are
 * outstanding for a given month, BEFORE they try to file the report and
 * hit a 412. Only used for the two per-facility templates.
 *
 * Props:
 *   templateCode: "DAILY_HYGIENE" | "CLEAN_FACILITY"
 */
export default function FacilityProgressDrawer({ templateCode }) {
  const { activeSiteId, isAllSites } = useSite();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // Default to the current calendar month so the drawer's first
  // expansion answers "how are we tracking this month?". The month
  // picker lets an owner check a past month before filing.
  const [periodMonth, setPeriodMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  const load = async () => {
    if (isAllSites) { setData(null); return; }  // per-site metric only
    setLoading(true); setErr("");
    try {
      const p = new URLSearchParams({ template_code: templateCode, period_month: periodMonth });
      if (activeSiteId) p.set("site_id", activeSiteId);
      const { data: d } = await api.get(`/checks/facility-progress?${p}`);
      setData(d);
    } catch (e) {
      setErr(e.response?.data?.detail || "Couldn't load facility progress.");
      setData(null);
    } finally { setLoading(false); }
  };

  // Fetch on first expansion + whenever site/month changes while open.
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, activeSiteId, periodMonth, templateCode]);

  if (isAllSites) return null;  // hidden in "All sites" mode — not meaningful

  const summary = data
    ? `${data.ready_count} of ${data.total_count} facilities complete for ${prettyMonth(data.period_month)}.`
    : "See which facility × days still need capture before you file.";

  return (
    <div className="vb-card" data-testid={`facility-progress-drawer-${templateCode.toLowerCase()}`}
      style={{ padding: 0, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 text-left px-4 py-3"
        data-testid="facility-progress-toggle"
        style={{ background: "transparent", border: 0, cursor: "pointer" }}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span className="text-sm font-semibold" style={{ color: "var(--vb-text-2)" }}>
          Monthly filing readiness
        </span>
        <span className="text-xs opacity-70 ml-2 truncate">{summary}</span>
      </button>

      {open && (
        <div className="px-4 pb-4" data-testid="facility-progress-body">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <label className="text-xs opacity-70">Period:</label>
            <input
              type="month" value={periodMonth}
              onChange={(e) => setPeriodMonth(e.target.value)}
              className="vb-input" style={{ maxWidth: 160 }}
              data-testid="facility-progress-month"
            />
            <span className="text-xs opacity-60">
              Expected days: <strong>{data?.expected_days ?? "—"}</strong>{" "}
              (current month counts only elapsed days)
            </span>
          </div>

          {err && <div className="vb-pill err mb-3">{err}</div>}
          {loading && <div className="text-xs opacity-60">Loading…</div>}

          {!loading && data && data.facilities.length === 0 && (
            <div className="text-xs opacity-70">
              No facility has {templateCode === "DAILY_HYGIENE" ? "hygiene" : "cleanliness"} tracking
              enabled for the current site. Enable at least one under Sites → Facilities before
              filing this report.
            </div>
          )}

          {!loading && data && data.facilities.length > 0 && (
            <div className="space-y-2">
              {data.facilities.map((f) => (
                <FacilityRow key={f.facility_id} f={f} />
              ))}
              <div className="text-xs opacity-60 mt-2">
                A month is ready to file when every enabled facility has ≥1 check on every day
                of the past calendar month. Missing days show as red pills below each bar.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FacilityRow({ f }) {
  const barColor = f.status === "ready" ? "#4F8A3A" : f.status === "attention" ? "#B4691D" : "#C45454";
  const Icon = f.status === "ready" ? CheckCircle2 : AlertTriangle;
  return (
    <div className="vb-card p-3" data-testid={`facility-progress-row-${f.facility_id}`}
      style={{ background: "var(--vb-bg)" }}>
      <div className="flex items-center gap-3 flex-wrap">
        <Icon size={16} style={{ color: barColor, flex: "0 0 auto" }} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate" style={{ color: "var(--vb-text-2)" }}>
            {f.facility_name}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <div style={{ flex: 1, height: 8, background: "var(--vb-border)", borderRadius: 4, overflow: "hidden", maxWidth: 320 }}>
              <div style={{ width: `${f.pct}%`, height: "100%", background: barColor, transition: "width 200ms" }} />
            </div>
            <span className="text-xs opacity-70 whitespace-nowrap">
              {f.days_captured}/{f.expected_days} days · {f.pct.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>
      {f.missing_days.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2" data-testid={`facility-progress-missing-${f.facility_id}`}>
          <span className="text-xs opacity-60 mr-1">Missing:</span>
          {f.missing_days.slice(0, 20).map((d) => (
            <span key={d} className="text-xs px-1.5 py-0.5 rounded"
              style={{ background: "rgba(196,84,84,0.12)", color: "#C45454" }}>
              {d}
            </span>
          ))}
          {f.missing_days.length > 20 && (
            <span className="text-xs opacity-60">+{f.missing_days.length - 20} more</span>
          )}
        </div>
      )}
    </div>
  );
}

function prettyMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  const names = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  return `${names[m - 1] || m} ${y}`;
}
